'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { FC, MouseEvent as ReactMouseEvent, useEffect, useLayoutEffect, useRef } from 'react';

import AnimatedLink from '@/components/shared/AnimatedLink';
import BookACallButton from '@/components/shared/Footer/BookACallButton';
import { FooterDto } from '@/components/shared/Footer/types';
import VideoBackgroundFooter from '@/components/shared/Footer/VideoBackground';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import { scrollToHash } from '@/helpers/scrollToHash';
import useDevice from '@/hooks/useDevice';
import { useModal } from '@/hooks/useModal';
import { gsap, ScrollTrigger } from '@/lib/gsap';

import FooterForm from './Form';
import css from './style.module.scss';
import { FooterVariant } from './variants';

interface Props {
	data: FooterDto;
	variant?: FooterVariant;
}

const Footer: FC<Props> = ({ data, variant = 'default' }) => {
	const { title, cta, socials, navigation } = data;
	const { openModal } = useModal();
	const device = useDevice();
	const cardRef = useRef<HTMLDivElement>(null);

	const isIndustriesLobbyPage = variant === 'industries-lobby';

	useLayoutEffect(() => {
		const title = cardRef?.current?.querySelector(`.${css.title}`);
		if (!title) return;

		const mm = gsap.matchMedia();

		mm.add(
			{
				isDesktop: '(min-width: 992px)'
			},
			context => {
				const { isDesktop } = context.conditions as gsap.Conditions;
				if (!isDesktop) return;

				const lines = title.querySelectorAll('p, div');

				gsap.set(lines, {
					yPercent: 100,
					opacity: 0
				});

				gsap.to(lines, {
					yPercent: 0,
					opacity: 1,
					duration: 0.8,
					ease: 'power2.out',
					stagger: {
						each: 0.08,
						from: 'start'
					},
					scrollTrigger: {
						trigger: title,
						start: 'top 90%',
						end: 'bottom 60%',
						toggleActions: 'play none none reverse'
					}
				});

				return () => {
					ScrollTrigger.getAll().forEach(t => t.kill());
				};
			}
		);

		return () => {
			mm.revert();
		};
	}, []);

	useEffect(() => {
		const card = cardRef.current;

		if (!card) return;

		const handleMouseMove = (e: MouseEvent) => {
			const { left, top } = card.getBoundingClientRect();
			const x = e.clientX - left;
			const y = e.clientY - top;

			gsap.to(card, {
				'--x': `${x}px`,
				'--y': `${y}px`,
				duration: 0.2
			});
		};

		card.addEventListener('mousemove', handleMouseMove);
		return () => card.removeEventListener('mousemove', handleMouseMove);
	}, []);

	const handleNavigationClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
		const href = event?.currentTarget?.getAttribute('href');

		if (href?.startsWith('#')) {
			event.preventDefault();

			scrollToHash(href);
		}
	};

	return (
		// id required for global styles
		<footer
			id="layout-footer"
			className={clsx(css.wrapper, isIndustriesLobbyPage && css.industriesLobbyPage)}
		>
			<VideoBackgroundFooter>
				<div ref={cardRef} className={css.formWrapper}>
					{device === 'desktop' && <div className={css.glowWrapper} />}

					<div>
						<div className={css.leftSide}>
							<div className={css.title}>
								{title ? <StrapiRichContent content={JSON.parse(JSON.stringify(title))} /> : null}
							</div>

							{cta ? (
								<BookACallButton
									className={css.bookACallButton}
									preview={cta.image}
									onClick={() => openModal('CALENDAR_BOOKING')}
								>
									{cta?.text}
								</BookACallButton>
							) : null}
						</div>

						<div className={css.rightSide}>
							<FooterForm />
						</div>
					</div>
				</div>

				<div className={css.bottomWrapper}>
					<ul className={css.mediaList}>
						{isArrayNotEmpty(socials) &&
							socials.map(({ id, name, url }, index) => (
								<li key={`${id}-${index}`}>
									{device === 'desktop' ? (
										<AnimatedLink href={url} target="_blank" rel="noreferrer">
											{name}
										</AnimatedLink>
									) : (
										<a href={url} target="_blank" rel="noreferrer">
											<Image
												src={`/assets/icons/socials/${name}.svg`}
												width="28"
												height="28"
												alt={name}
											/>
										</a>
									)}
								</li>
							))}
					</ul>

					<div className={css.navContainer}>
						{isArrayNotEmpty(navigation) &&
							navigation.map(({ navigationItem }, index) => (
								<ul key={index} className={css.navList}>
									{navigationItem?.map(({ id, text, url }) => (
										<li key={id}>
											<AnimatedLink
												target={url.includes('http') ? '_blank' : '_self'}
												href={url}
												onClick={handleNavigationClick}
											>
												{text}
											</AnimatedLink>
										</li>
									))}
								</ul>
							))}
					</div>

					{device === 'desktop' && <span className={css.copy}>{new Date().getFullYear()}</span>}
				</div>
			</VideoBackgroundFooter>
		</footer>
	);
};

export default Footer;
